import * as React from "react"
import * as MDT from "models/mdt";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { ReviewsByServiceChart } from "charts/mdt/ReviewsByServiceChart";
import { FormControlLabel, Grid, Paper, Switch } from "@mui/material";
import { MDTActivityTrendChart } from "charts/mdt/MDTActivityTrendChart";
import { PatientsBySiteChart } from "charts/mdt/PatientsBySiteChart";
import { FilterContext, IFilterKeys, IFilters } from "models/core/filter";
import { PatientsByMorphChart } from "charts/mdt/PatientsByMorphChart";
import { HHSResidenceGroupPieChart } from "charts/mdt/HHSResidenceGroupPieChart";
import { HHSResidenceGroupByYearChart } from "charts/mdt/HHSResidenceGroupByYearChart";
import { TumourStageChart } from "charts/mdt/TumourStageChart";
import { TumourGradeChart } from "charts/mdt/TumourGradeChart";
import { AgeAtDiagnosisChart } from "charts/common/AgeAtDiagnosisChart";
import { SexChart } from "charts/common/SexChart";
import { ECOGChart } from "charts/common/ECOGChart";
import { ComorbiditiesChart } from "charts/common/ComorbiditiesChart";
import { FirstNationsStatusChart } from "charts/common/FirstNationsStatusChart";
import { SESChart } from "charts/common/SESChart";
import { RemotenessChart } from "charts/mdt/RemotenessChart";
import { DataQualityByIndicatorChart } from "charts/mdt/DataQualityByIndicatorChart";
import { DataQualityByPeriodChart } from "charts/mdt/DataQualityByPeriod";
import { TimeToFirstTreatment } from "models/mdt/plotdata";
import { TimeToFirstTreatmentChart } from "charts/mdt/TimeToFirstTreatmentChart";
import { FirstTreatmentChart } from "charts/mdt/FirstTreatmentChart";
import { Link } from "react-scroll";
import { DashChip } from "components/common/DashChips";
import { OneYearSurvivalChart } from "charts/mdt/OneYearSurvivalChart";
import { ThreeYearSurvivalChart } from "charts/mdt/ThreeYearSurvivalChart";
import { FiveYearSurvivalChart } from "charts/mdt/FiveYearSurvivalChart";
import { HHSOfResidenceChart } from "charts/common/HHSOfResidenceChart";
import { KMSurvivalChart } from "charts/mdt/KMSurvivalChart";
import { Check, Clear } from "@mui/icons-material";
import Loader from "pages/shared/pageloader";


export function MdtDashboard() {
	const { state, dispatch } = React.useContext(AppContext);

	const [showFilterChips, setFilterChips] = React.useState(true);

	const addFilter: MDT.AddFilterFunc = async (key: keyof typeof MDT.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
		dispatch({ type: ActionTypes.ComputingChartData });
		const filteredDashboard = await MDT.computeChartData(MDT.addFilter(state.page.dashboard as MDT.IMDTDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	const removeFilter: MDT.RemoveFilterFunc = async (key: keyof typeof MDT.DashboardFilterKeys, value?: string) => {
		dispatch({ type: ActionTypes.ComputingChartData });
		const filteredDashboard = await MDT.computeChartData(MDT.removeFilter(state.page.dashboard as MDT.IMDTDashboard, key, value));
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	React.useEffect(() => {
		dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });
		MDT.loadMdtDashboard(state.page.dashboard as MDT.IMDTDashboard)
			.then((value) => {
				dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value })
			});
		return () => {
			MDT.closeMdtDashboard();
		}
	}, []);


	const mdtData = state.page.dashboard.chartData as MDT.IChartData;



	const inferSurvivalData = (): { mdtName: string, cancerName: string, cancerNameKey: string } => {
		let survivalData = {
			mdtName: "",
			cancerName: "",
			cancerNameKey: ""
		};

		if (state.page.loading || state.page.computing)
			return survivalData;

		const activeFilters = state.page?.dashboard?.filters;

		survivalData.mdtName =
			activeFilters?.Mdt?.value[0] ?? mdtData?.patientsByService.labels[mdtData?.patientsByService?.labels.length - 1];


		const getPrimarySite = () => {
			if (mdtData?.primarySite) {

				for (let i = mdtData.primarySite.labels.length - 1; i >= 0; i--) {
					if (mdtData.primarySite.labels[i] != "Not Determined")
						return mdtData.primarySite.labels[i];
				}

			}
			return "";
		}

		const getPrimarySiteFromFilters = () => {
			if (activeFilters?.PrimarySite?.parts) {
				if (activeFilters?.PrimarySite?.parts?.length == 2) {
					return activeFilters?.PrimaySite?.parts[1]
				}
				return activeFilters?.PrimarySite?.parts[0]
			}

			return "";
		}

		const getPrimarySiteKeyFromFilters = () => {
			let key = "";
			if (activeFilters?.PrimarySite) {
				key = activeFilters?.PrimarySite?.parts[0];
				if (activeFilters?.PrimarySite?.parts?.length == 2) {
					key += activeFilters?.PrimarySite?.parts[1];
				}
			}
			return key;
		}

		survivalData.cancerName = getPrimarySiteFromFilters()
			|| getPrimarySite();

		survivalData.cancerNameKey = getPrimarySiteKeyFromFilters()
			|| getPrimarySite();


		return survivalData;
	}

	const survivalData = inferSurvivalData();

	React.useEffect(() => {
		if (survivalData.cancerName && survivalData.mdtName) {
			MDT.loadSurvivalDashboard(state.page.dashboard as MDT.IMDTDashboard, survivalData.mdtName, survivalData.cancerName, state.page.dashboard.filters)
				.then((value) => {
					dispatch({ type: ActionTypes.UserClickedOnChart, payload: value })
				});
		}

	}, [state.page.loading || state.page.computing])


	if (state.page.loading || !state.page.dashboard.chartData)
		return <Loader />

	//Create function to show/hide filter chips
	const toggleChips = () => {
		if (showFilterChips) {
			setFilterChips(false);
		} else {
			setFilterChips(true);
		}
	};

	let activeFiltersArray = [];
	let filterArray = [];
	let chips = [];

	for (const key in state.page.dashboard.filters) {
		if (Object.prototype.hasOwnProperty.call(state.page.dashboard.filters, key)) {
			activeFiltersArray.push({ "key": key, "values": state.page.dashboard.filters[key].value, "parts": state.page.dashboard.filters[key].parts });
		}
	}

	for (const key in MDT.DashboardFilterKeys) {
		if (Object.prototype.hasOwnProperty.call(MDT.DashboardFilterKeys, key)) {
			filterArray.push({ "key": key, "label": MDT.DashboardFilterKeys[key].label });
		}
	}

	activeFiltersArray.forEach(a => {
		a.values.forEach(b => {
			chips.push(<DashChip key={a.key + b}
				removeFilterFn={removeFilter}
				filterName={filterArray.find(id => id.key == a.key).label}
				filterValue={b}
				filterId={a.key}
				filterParent={(a.parts.length > 1 ? a.parts[0] : null)}
			/>
			)
		})
	})


	const survivalDataSelections = () => {
		const mdt =
			survivalData.mdtName ?
				<><Check color={"success"} /> MDT: {survivalData.mdtName}</>
				: <><Clear color={"warning"} /> MDT: Please select a MDT</>

		const cancer =
			survivalData.cancerName ?
				<><Check color={"success"} /> Cancer: {survivalData.cancerName}</>
				: <><Clear color={"warning"} /> Cancer: Please select a cancer type</>

		return (
			<>
				{mdt}
				<span style={{ padding: 5 }}>&nbsp;</span>
				{cancer}
			</>
		)
	}




	const dashboard =
		<>
			<div style={{ position: "sticky", top: "64px", left: "10px", zIndex: 2 }}>
				<Paper className="dash-header">


					<div>
						<Link activeClass="active" smooth spy to="Overview" style={{ paddingRight: "15px", color: "black" }}>
							Overview
						</Link>
						<Link activeClass="active" smooth spy to="TumourCx" style={{ paddingRight: "15px", color: "black" }}>
							Tumour Characteristics
						</Link>
						<Link activeClass="active" smooth spy to="PatientCx" style={{ paddingRight: "15px", color: "black" }}>
							Patient Characteristics
						</Link>
						{/* <Link activeClass="active" smooth spy to="Quality" style={{ paddingRight: "15px", color: "black" }}>
							Quality Measures
						</Link> */}
						<Link activeClass="active" smooth spy to="Outcomes" style={{ paddingRight: "15px", color: "black" }}>
							Outcomes
						</Link>
					</div>

					<div>
						<FormControlLabel
							control={
								<Switch
									checked={showFilterChips}
									onChange={_ => toggleChips()}
								/>
							}
							label="Show Filter Selections"
						/>
					</div>




				</Paper>

				{(showFilterChips ?
					<div style={{ margin: 10 }}>
						{chips}
					</div> : <div></div>)}


			</div>



			<Grid container spacing={2}>

				<Grid container id="Overview">
					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<MDTActivityTrendChart
							plotDataReviews={mdtData.reviewsByPeriod}
							plotDataPatients={mdtData.patientsByPeriod}
							plotDataDiagnoses={mdtData.diagnosesByPeriod}
							activeFilters={state.page.dashboard.filters}
							noProfileCount={mdtData.totalNoProfiles}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<ReviewsByServiceChart
							plotDataReviews={mdtData.reviewsByService}
							plotDataPatients={mdtData.patientsByService}
							plotDataDiagnoses={mdtData.diagnosesByService}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<HHSResidenceGroupPieChart
							plotData={mdtData.hhsResidenceGroup}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					{/* <Grid item xs={window.innerWidth > 991 ? 4 : 11}>
				<HHSResidenceGroupByYearChart 
					plotData={mdtChartData.hhsResidenceGroupByPeriod}
					activeFilters = {state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn ={removeFilter}
				/>
			</Grid>	 */}
				</Grid>
				{
					//Tumour Characteristics
				}
				<Grid container id="TumourCx">
					<Grid item xs={11}>
						<h1 style={{ marginLeft: "10px", marginBottom: "20px" }}>Tumour Characteristics</h1>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<PatientsBySiteChart
							plotData={mdtData.primarySite}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<PatientsByMorphChart
							plotData={mdtData.morphology}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<TumourStageChart
							plotData={mdtData.tnmStage}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					{/* <Grid item xs={window.innerWidth > 991 ? 3 : 11}>
			<TumourGradeChart 
				plotData={state.page.dashboard.chartData.tnmStage}
				activeFilters = {state.page.dashboard.filters}
				addFilterFn={addFilter}
				removeFilterFn ={removeFilter}
			/>
		</Grid>	 */}
				</Grid>
				{
					//Patient Characteristics
				}
				<Grid container id="PatientCx">
					<Grid item xs={11}>
						<h1 style={{ marginLeft: "10px", marginBottom: "20px" }}>Patient Characteristics</h1>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<AgeAtDiagnosisChart
							plotData={mdtData.ageRange}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<SexChart
							plotData={mdtData.sex}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<ECOGChart
							plotData={mdtData.ecogGroup}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<ComorbiditiesChart
							plotData={mdtData.comorbidities}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<FirstNationsStatusChart
							plotData={mdtData.indigenousGroup}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<SESChart
							plotData={mdtData.ses}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<RemotenessChart
							plotData={mdtData.remoteness}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<HHSOfResidenceChart
							plotData={mdtData.hhsOfResidence}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>
				</Grid>
				{
					//Quality Measures
				}
				{/* <Grid container id="Quality">
					<Grid item xs={11}>
						<h1 style={{ marginLeft: "10px", marginBottom: "20px" }}>Quality Measures</h1>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 6 : 11}>
						<DataQualityByIndicatorChart
							plotData={mdtData.dataQualityByIndicator}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 6 : 11}>
						<DataQualityByPeriodChart
							plotData={mdtData.dataQualityByPeriod}
							plotDataFacility={mdtData.dqCompFacility}
							plotDataState={mdtData.dqCompState}
							mdtFacilityRef={mdtData.mdts}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid> */}

					{/* <Grid item xs={window.innerWidth > 991 ? 3 : 11}>
				<TimeToFirstTreatmentChart 
					plotData={mdtData.timeToFirstTreatment}
					activeFilters = {state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn ={removeFilter}
				/>
			</Grid>	

			<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
				<FirstTreatmentChart 
					plotData={mdtData.firstTreatment}
					activeFilters = {state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn ={removeFilter}
				/>
			</Grid>	 */
				/* </Grid> */}

				{
					//Outcomes
				}
				<Grid container id="Outcomes">
					<Grid item xs={11}>
						<h1 style={{ marginLeft: "10px", marginBottom: "-10px" }}>Outcomes</h1>
					</Grid>

					<Grid item xs={11}>
						<h2 style={{ marginLeft: "10px", marginBottom: "20px", fontSize: "0.9em" }}>
							Note: These graphs are static and do not respond to filter selections with the exception of My MDT and Cancer Type
							<br />
							If no selections are made, the survival charts default to your most common MDT and Cancer Type
						</h2>
						<div style={{ marginLeft: 10, display: "flex" }}>
							{survivalDataSelections()}
						</div>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<OneYearSurvivalChart
							plotData={mdtData.oneYearSurvivalMDT}
							mdtFacilityRef={mdtData.mdts}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
							mdtName={survivalData.mdtName}
							cancerName={survivalData.cancerNameKey}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<ThreeYearSurvivalChart
							plotData={mdtData.threeYearSurvivalMDT}
							mdtFacilityRef={mdtData.mdts}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
							mdtName={survivalData.mdtName}
							cancerName={survivalData.cancerNameKey}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<FiveYearSurvivalChart
							plotData={mdtData.fiveYearSurvivalMDT}
							mdtFacilityRef={mdtData.mdts}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
							mdtName={survivalData.mdtName}
							cancerName={survivalData.cancerNameKey}
						/>
					</Grid>

					{/* <Grid item xs={window.innerWidth > 991 ? 3 : 11}>
				<TenYearSurvivalChart 
					plotData={mdtData.tenYearSurvivalMDT}
					mdtFacilityRef = {mdtData.mdts}
					activeFilters = {state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn ={removeFilter}
				/>
			</Grid>	 */}

					<Grid item xs={window.innerWidth > 991 ? 6 : 11}>
						<KMSurvivalChart
							plotData={mdtData.kmSurvivalMDT}
							mdtFacilityRef={mdtData.mdts}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
							mdtName={survivalData.mdtName}
							cancerName={survivalData.cancerNameKey}
						/>
					</Grid>
				</Grid>

			</Grid>
		</>

	return (
		dashboard
	)


} 